import { useState, useEffect, useMemo, useCallback } from "react";
import axios from "axios";
import { API } from "@/App";
import { toast } from "sonner";

/**
 * useColumnManager — Manages column visibility, ordering, and persistence.
 * Replaces ~300 lines of duplicated code across DayView, Upcoming, History.
 *
 * @param {string} pageKey - Unique key for saving preferences (e.g., "today", "upcoming", "history")
 * @param {Array} defaultColumns - Array of { id, label } column definitions
 * @param {Array} customizableIds - Column IDs that can be hidden/shown (subset of defaultColumns)
 */
export function useColumnManager(pageKey, defaultColumns, customizableIds = []) {
  const [hiddenCols, setHiddenCols] = useState([]);
  const [colOrder, setColOrder] = useState(defaultColumns.map((c) => c.id));
  const [customizeOpen, setCustomizeOpen] = useState(false);
  const [rearrangeOpen, setRearrangeOpen] = useState(false);
  const [tempHidden, setTempHidden] = useState([]);
  const [tempOrder, setTempOrder] = useState([]);
  const [dragIdx, setDragIdx] = useState(null);
  const [overIdx, setOverIdx] = useState(null);

  // Load saved preferences from backend
  useEffect(() => {
    const loadPrefs = async () => {
      try {
        const res = await axios.get(`${API}/user-preferences/${pageKey}`, { withCredentials: true });
        if (res.data.hidden_columns?.length) setHiddenCols(res.data.hidden_columns);
        if (res.data.column_order?.length) {
          // Append any columns added since this preference was saved, so new
          // columns appear instead of being silently dropped from the order.
          const saved = res.data.column_order;
          const missing = defaultColumns.map(c => c.id).filter(id => !saved.includes(id));
          setColOrder([...saved, ...missing]);
        }
      } catch {}
    };
    loadPrefs();
  }, [pageKey, defaultColumns]);

  // Save preferences to backend
  const savePrefs = useCallback(async (hidden, order) => {
    try {
      await axios.put(
        `${API}/user-preferences/${pageKey}`,
        { hidden_columns: hidden, column_order: order },
        { withCredentials: true }
      );
    } catch {}
  }, [pageKey]);

  // Computed visible columns in user's order
  const visibleCols = useMemo(() => {
    return colOrder
      .filter((id) => !hiddenCols.includes(id))
      .map((id) => defaultColumns.find((c) => c.id === id))
      .filter(Boolean);
  }, [colOrder, hiddenCols, defaultColumns]);

  // Open customize modal
  const openCustomize = useCallback(() => {
    setTempHidden([...hiddenCols]);
    setCustomizeOpen(true);
  }, [hiddenCols]);

  // Save customize (visibility)
  const saveCustomize = useCallback(() => {
    setHiddenCols(tempHidden);
    savePrefs(tempHidden, colOrder);
    setCustomizeOpen(false);
    toast.success("Column visibility saved");
  }, [tempHidden, colOrder, savePrefs]);

  // Toggle a column in tempHidden
  const toggleTempCol = useCallback((id) => {
    setTempHidden((prev) =>
      prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]
    );
  }, []);

  // Open rearrange modal
  const openRearrange = useCallback((orderOverride) => {
    const order = orderOverride || colOrder.filter((id) => !hiddenCols.includes(id));
    setTempOrder(order);
    setRearrangeOpen(true);
  }, [colOrder, hiddenCols]);

  // Save rearrange (order)
  const saveRearrange = useCallback(() => {
    setColOrder(tempOrder);
    savePrefs(hiddenCols, tempOrder);
    setRearrangeOpen(false);
    toast.success("Column order saved");
  }, [tempOrder, hiddenCols, savePrefs]);

  // Drag-and-drop handlers for rearrange
  const onDragStart = useCallback((idx) => setDragIdx(idx), []);
  const onDragEnd = useCallback(() => { setDragIdx(null); setOverIdx(null); }, []);
  // Which gap the insertion line is drawn in while dragging.
  const onDragOverIdx = useCallback((idx) => setOverIdx(idx), []);
  const onDrop = useCallback((targetIdx) => {
    if (dragIdx === null || dragIdx === targetIdx) return;
    const copy = [...tempOrder];
    const [moved] = copy.splice(dragIdx, 1);
    copy.splice(targetIdx, 0, moved);
    setTempOrder(copy);
    setDragIdx(null);
    setOverIdx(null);
  }, [dragIdx, tempOrder]);

  return {
    // State
    hiddenCols,
    colOrder,
    visibleCols,
    customizeOpen,
    rearrangeOpen,
    tempHidden,
    tempOrder,
    dragIdx,
    overIdx,

    // Setters (for advanced use cases like Upcoming's dual-order)
    setColOrder,
    setCustomizeOpen,
    setRearrangeOpen,
    setTempOrder,

    // Actions
    openCustomize,
    saveCustomize,
    toggleTempCol,
    openRearrange,
    saveRearrange,
    onDragStart,
    onDragEnd,
    onDragOverIdx,
    onDrop,
    savePrefs,
  };
}
